import appConstants from '../../../../../../../appConstants';
import { isGroupHeaders, isGroupedPickups, isSingleInGroupPickups } from '../utils';
import { PICKUP_MANAGEMENT_MODES } from '../../PickupManagement/constants';

/**
 * SelectionManager - Handles all selection-related logic for GroupPickup component
 * 
 * This manager handles:
 * - Individual pickup selection
 * - Group selection
 * - Shift-click range selection
 * - Cross-selection between groups and pickups
 * - Grid selection state synchronization
 */
export class SelectionManager {
  constructor(component) {
    this.component = component;
  }

  // Helper method to extract pickup IDs from a group - handles all row types
  getGroupPickupIds = (groupId) => {
    const allPickups = this.component.props.DispatchStore.pickupsList || [];
    const pickupIds = new Set();

    // Single pass through pickups - optimized
    allPickups
      .filter(pickup => pickup.groupId === groupId)
      .forEach((pickup) => {
        if (isGroupHeaders(pickup) && pickup.pickupNumberIdList) {
          // Group header - use pickupNumberIdList
          pickup.pickupNumberIdList
            .filter(p => p?.pickupId)
            .forEach((p) => {
              pickupIds.add(p.pickupId.toLowerCase());
            });
        } else if (pickup.pickupId) {
          // Individual pickup or grouped pickup - use pickupId
          pickupIds.add(pickup.pickupId.toLowerCase());
        }
      });

    return Array.from(pickupIds);
  };

  // Optimized: Batch collect pickup IDs from multiple groups in single pass
  collectPickupIdsFromGroups = (groupIds) => {
    if (!groupIds || groupIds.length === 0) return new Set();
    
    const allPickups = this.component.props.DispatchStore.pickupsList || [];
    const groupIdsSet = new Set(groupIds);
    const pickupIds = new Set();

    // Single pass through all pickups
    allPickups
      .filter(pickup => pickup.groupId && groupIdsSet.has(pickup.groupId))
      .forEach((pickup) => {
        if (isGroupHeaders(pickup) && pickup.pickupNumberIdList) {
          pickup.pickupNumberIdList
            .filter(p => p?.pickupId)
            .forEach((p) => {
              pickupIds.add(p.pickupId.toLowerCase());
            });
        } else if (pickup.pickupId) {
          pickupIds.add(pickup.pickupId.toLowerCase());
        }
      });

    return pickupIds;
  };

  // Optimized: Build visible rows array in single pass
  buildVisibleRows = () => {
    const visibleRows = [];
    this.component.gridApi.forEachNodeAfterFilterAndSort((node) => {
      const rowData = node.data;
      if (isGroupHeaders(rowData)) {
        visibleRows.push({
          type: 'group',
          groupId: rowData.id,
          rowIndex: node.rowIndex
        });
      } else if (isGroupedPickups(rowData) || isSingleInGroupPickups(rowData)) {
        const pickupId = rowData.pickupId || rowData.id;
        if (pickupId) {
          visibleRows.push({
            type: 'pickup',
            pickupId: pickupId.toLowerCase(),
            groupId: rowData.groupId,
            rowIndex: node.rowIndex
          });
        }
      }
    });
    return visibleRows;
  };

  // Optimized: Collect pickup IDs in range with single pass
  collectPickupsInRange = (visibleRows, minRowIndex, maxRowIndex, additionalGroupIds = []) => {
    const rangePickupIds = new Set();
    const groupsInRange = new Set(additionalGroupIds);
    
    // Single pass through visible rows
    visibleRows
      .filter(row => row.rowIndex >= minRowIndex && row.rowIndex <= maxRowIndex)
      .forEach((row) => {
        if (row.type === 'pickup') {
          rangePickupIds.add(row.pickupId);
          if (row.groupId) groupsInRange.add(row.groupId);
        } else if (row.type === 'group') {
          groupsInRange.add(row.groupId);
        }
      });

    // Batch collect pickup IDs from all groups in range
    if (groupsInRange.size > 0) {
      const groupPickupIds = this.collectPickupIdsFromGroups(Array.from(groupsInRange));
      groupPickupIds.forEach(id => rangePickupIds.add(id));
    }

    return rangePickupIds;
  };

  // Consolidated: Apply grid selection using AG-Grid's built-in APIs
  applyGridSelection = (selectedPickupIds, options = {}) => {
    if (!this.component.gridApi || !selectedPickupIds || selectedPickupIds.length === 0) {
      if (options.onComplete) options.onComplete();
      return;
    }

    const {
      clearGroupAnchor = false,
      updateLastSelectedPickup = null,
      updateLastSelectedGroup = null,
      skipValidation = false
    } = options;

    this.component.isGroupSelectionInProgress = true;

    // Update store first
    this.component.props.glEventHub.emit('UpdateDispatchStore', 'updateSelectedPickups', [selectedPickupIds]);

    setTimeout(() => {
      // Use AG-Grid's deselectAll
      this.component.gridApi.deselectAll();
      
      // Collect nodes to select (AG-Grid's forEachNode is still needed for hidden nodes)
      const nodesToSelect = [];
      const selectedPickupIdsLower = selectedPickupIds.map(id => id.toLowerCase());
      this.component.gridApi.forEachNode((node) => {
        const rowData = node.data;
        if (!isGroupHeaders(rowData) && (isGroupedPickups(rowData) || isSingleInGroupPickups(rowData))) {
          const pickupId = (rowData.pickupId || rowData.id)?.toLowerCase();
          if (pickupId && selectedPickupIdsLower.includes(pickupId)) {
            nodesToSelect.push(node);
          }
        }
      });

      // Use AG-Grid's setSelected() on each node (more efficient than individual calls)
      nodesToSelect.forEach(node => node.setSelected(true, false));

      // Validation
      if (!skipValidation && nodesToSelect.length === 0 && selectedPickupIds.length > 0) {
        console.warn('GroupPickup: No pickup rows selected', {
          expected: selectedPickupIds.length
        });
      }

      this.component.groupDataManager.updateGridContext(true);
      this.updateGridSelectionState();
      this.emitSelectionEvents();

      // Update anchor points
      if (clearGroupAnchor) this.component.lastSelectedGroupId = null;
      if (updateLastSelectedPickup) this.component.lastSelectedPickupId = updateLastSelectedPickup.toLowerCase();
      if (updateLastSelectedGroup) this.component.lastSelectedGroupId = updateLastSelectedGroup;

      setTimeout(() => {
        this.component.isGroupSelectionInProgress = false;
        if (options.onComplete) options.onComplete();
      }, 0);
    }, 0);
  };

  // Optimized computed getter for selected groups - handles all selection cases
  get selectedGroups() {
    const selectedPickupIds = this.component.props.DispatchStore.selectedPickupIdList || [];
    if (selectedPickupIds.length === 0) return new Set();

    const allPickups = this.component.props.DispatchStore.pickupsList || [];
    const selectedGroups = new Set();
    const selectedPickupIdsSet = new Set(selectedPickupIds.map((id) => id.toLowerCase()));

    // Build group-to-pickups map in single pass - O(n)
    const groupPickupsMap = new Map();
    allPickups.forEach((pickup) => {
      if (pickup.groupId) {
        if (!groupPickupsMap.has(pickup.groupId)) {
          groupPickupsMap.set(pickup.groupId, new Set());
        }
        // Collect pickup IDs for this group
        if (isGroupHeaders(pickup) && pickup.pickupNumberIdList) {
          pickup.pickupNumberIdList.forEach((p) => {
            if (p.pickupId) {
              groupPickupsMap.get(pickup.groupId).add(p.pickupId.toLowerCase());
            }
          });
        } else if (pickup.pickupId) {
          groupPickupsMap.get(pickup.groupId).add(pickup.pickupId.toLowerCase());
        }
      }
    });

    // Check each group to see if all its pickups are selected - O(n)
    groupPickupsMap.forEach((groupPickupIdsSet, groupId) => {
      if (groupPickupIdsSet.size > 0) {
        // Check if all pickups in group are selected using Set operations
        const allSelected = Array.from(groupPickupIdsSet).every(pickupId => 
          selectedPickupIdsSet.has(pickupId)
        );
        if (allSelected) {
          selectedGroups.add(groupId);
        }
      }
    });

    return selectedGroups;
  }

  // Unified selection handler for all group types and scenarios
  handleSelection = (event) => {
    if (!this.component.gridApi || this.component.isGroupSelectionInProgress || this.component.isExpandCollapseInProgress) return;

    const rowData = event?.data;
    if (!rowData) return;

    // Check if this is an expand/collapse button click or first column click
    const isExpandCollapseClick = this.isExpandCollapseButtonClick(event);
    if (isExpandCollapseClick) {
      return; // Skip selection for expand/collapse clicks
    }

    const isCtrlClick = event?.event?.ctrlKey || event?.event?.metaKey;
    const isShiftClick = event?.event?.shiftKey;
    const isGroupHeader = isGroupHeaders(rowData);
    const isPickupRow = isGroupedPickups(rowData) || isSingleInGroupPickups(rowData);

    // For shift clicks on pickup rows
    if (isShiftClick && isPickupRow) {
      const pickupId = rowData.pickupId || rowData.id;
      // Check if we have a group anchor - if so, handle as group-to-pickup shift selection
      if (this.component.lastSelectedGroupId) {
        this.handleGroupToPickupShiftSelection(pickupId);
      } else {
        this.handlePickupShiftSelection(pickupId);
      }
      return;
    }

    // For group headers with shift
    if (isShiftClick && isGroupHeader) {
      // Check if we have a pickup anchor - if so, handle as pickup-to-group shift selection
      if (this.component.lastSelectedPickupId && !this.component.lastSelectedGroupId) {
        this.handlePickupToGroupShiftSelection(rowData.id);
      } else {
        this.handleGroupShiftSelection(rowData.id);
      }
      return;
    }

    if (isGroupHeader) {
      this.handleGroupSelection(rowData.id, isCtrlClick);
    } else if (isPickupRow) {
      const pickupId = rowData.pickupId || rowData.id;
      this.handlePickupSelection(pickupId, isCtrlClick);
    }
  };

  // Check if the click was on the expand/collapse button or first column
  isExpandCollapseButtonClick = (event) => {
    const target = event?.event?.target;
    if (!target) return false;

    // Check for expand/collapse button selectors
    const isExpandButtonClick =
      target?.closest('.expandButton') || // Custom class from GroupCellRenderer
      target?.closest('[data-col-id="isMergedPickup"]') || // AG-Grid column identifier
      target?.closest('.ag-cell[col-id="isMergedPickup"]') || // AG-Grid cell class
      target?.closest('.ag-group-expanded') || // AG-Grid expand icon
      target?.closest('.ag-group-contracted') || // AG-Grid collapse icon
      target?.closest('.ag-icon') || // AG-Grid icons
      target?.closest('[class*="Arrow"]'); // Material-UI arrow icons

    // Check if click is on first column of group header
    const isFirstColumnClick =
      target?.closest('.ag-cell') &&
      target?.closest('.ag-cell').getAttribute('col-id') === 'isMergedPickup';

    return isExpandButtonClick || isFirstColumnClick;
  };

  // Handle group selection (works for both expanded and collapsed groups)
  handleGroupSelection = (groupId, isMultiSelect) => {
    const currentSelectedPickupIds = this.component.props.DispatchStore.selectedPickupIdList || [];
    const groupPickupIds = this.getGroupPickupIds(groupId);

    // Use Set for O(1) lookup instead of array.includes (O(n))
    const selectedPickupIdsSet = new Set(currentSelectedPickupIds.map((id) => id.toLowerCase()));
    const isGroupSelected =
      groupPickupIds.length > 0 &&
      groupPickupIds.every((pickupId) => selectedPickupIdsSet.has(pickupId));

    if (isMultiSelect) {
      this.toggleGroupSelection(groupId, !isGroupSelected);
    } else {
      this.selectSingleGroup(groupId);
      // Update last selected group for shift range selection
      this.component.lastSelectedGroupId = groupId;
      // Also set lastSelectedPickupId to first pickup in group for cross-selection support
      if (groupPickupIds.length > 0) {
        this.component.lastSelectedPickupId = groupPickupIds[0].toLowerCase();
      }
    }
  };

  // Handle individual pickup selection (works for both expanded and collapsed groups)
  handlePickupSelection = (pickupId, isMultiSelect) => {
    const currentSelectedPickupIds = this.component.props.DispatchStore.selectedPickupIdList || [];
    const pickupIdLower = pickupId.toLowerCase();
    // Use Set for O(1) lookup instead of array.includes (O(n))
    const selectedSet = new Set(currentSelectedPickupIds);
    const isCurrentlySelected = selectedSet.has(pickupIdLower);

    this.component.isGroupSelectionInProgress = true;
    let newSelectedPickups;

    if (isMultiSelect) {
      if (isCurrentlySelected) {
        selectedSet.delete(pickupIdLower);
        newSelectedPickups = Array.from(selectedSet);
      } else {
        selectedSet.add(pickupIdLower);
        newSelectedPickups = Array.from(selectedSet);
      }
    } else {
      newSelectedPickups = [pickupIdLower];
      // Update last selected pickup for shift range selection (only for normal selection, not ctrl)
      this.component.lastSelectedPickupId = pickupIdLower;
      // Clear group anchor when selecting a pickup directly
      this.component.lastSelectedGroupId = null;
    }

    this.component.props.glEventHub.emit('UpdateDispatchStore', 'updateSelectedPickups', [
      newSelectedPickups
    ]);

    setTimeout(() => {
      this.component.groupDataManager.updateGridContext();
      this.updateGridSelectionState();
      this.emitSelectionEvents();

      setTimeout(() => {
        this.component.isGroupSelectionInProgress = false;
      }, 100);
    }, 100);
  };

  // Handle shift range selection for pickup rows
  handlePickupShiftSelection = (endPickupId) => {
    if (!this.component.gridApi) return;

    const currentSelectedPickupIds = this.component.props.DispatchStore.selectedPickupIdList || [];
    const anchorGroupId = this.component.lastSelectedGroupId;
    
    // Find anchor pickup: lastSelectedPickupId -> first from anchorGroup -> last in current selection
    let lastSelectedPickupId = this.component.lastSelectedPickupId || 
      (anchorGroupId && this.getGroupPickupIds(anchorGroupId)[0]?.toLowerCase()) ||
      (currentSelectedPickupIds.length > 0 && currentSelectedPickupIds[currentSelectedPickupIds.length - 1].toLowerCase());
    
    if (!lastSelectedPickupId) {
      this.handlePickupSelection(endPickupId, false);
      return;
    }

    const visibleRows = this.buildVisibleRows();
    const visiblePickupRows = visibleRows.filter(r => r.type === 'pickup');
    const endIndex = visiblePickupRows.findIndex(p => p.pickupId === endPickupId.toLowerCase());
    
    // Handle collapsed group case: anchor not visible but group header is
    if (endIndex !== -1 && anchorGroupId) {
      const startIndex = visiblePickupRows.findIndex(p => p.pickupId === lastSelectedPickupId.toLowerCase());
      if (startIndex === -1) {
        const anchorGroupRow = visibleRows.find(r => r.type === 'group' && r.groupId === anchorGroupId);
        const endRowIndex = visiblePickupRows[endIndex]?.rowIndex;
        if (anchorGroupRow && endRowIndex !== undefined) {
          const rangePickupIds = this.collectPickupsInRange(
            visibleRows, 
            Math.min(anchorGroupRow.rowIndex, endRowIndex), 
            Math.max(anchorGroupRow.rowIndex, endRowIndex), 
            [anchorGroupId]
          );
          this.applyGridSelection(Array.from(rangePickupIds), {
            clearGroupAnchor: true,
            updateLastSelectedPickup: endPickupId
          });
          return;
        }
      }
    }

    // Normal range selection
    const startIndex = visiblePickupRows.findIndex(p => p.pickupId === lastSelectedPickupId.toLowerCase());
    if (startIndex === -1 || endIndex === -1) {
      this.handlePickupSelection(endPickupId, false);
      return;
    }

    const minIndex = Math.min(startIndex, endIndex);
    const maxIndex = Math.max(startIndex, endIndex);
    const visibleRangePickupIds = visiblePickupRows.slice(minIndex, maxIndex + 1).map(p => p.pickupId);
    const startRowIndex = visiblePickupRows[minIndex]?.rowIndex;
    const endRowIndex = visiblePickupRows[maxIndex]?.rowIndex;

    // Collect range pickups
    const anchorGroupIds = anchorGroupId ? [anchorGroupId] : [];
    const rangePickupIds = startRowIndex !== undefined && endRowIndex !== undefined
      ? this.collectPickupsInRange(visibleRows, Math.min(startRowIndex, endRowIndex), Math.max(startRowIndex, endRowIndex), anchorGroupIds)
      : new Set(visibleRangePickupIds);
    
    visibleRangePickupIds.forEach(id => rangePickupIds.add(id));

    this.applyGridSelection(Array.from(rangePickupIds), {
      clearGroupAnchor: true,
      updateLastSelectedPickup: endPickupId
    });
  };

  // Handle shift selection from group header to pickup
  handleGroupToPickupShiftSelection = (endPickupId) => {
    if (!this.component.gridApi || !this.component.lastSelectedGroupId) {
      this.handlePickupShiftSelection(endPickupId);
      return;
    }

    // Get first pickup from the anchor group as the start point
    const groupPickupIds = this.getGroupPickupIds(this.component.lastSelectedGroupId);
    if (groupPickupIds.length === 0) {
      this.handlePickupShiftSelection(endPickupId);
      return;
    }

    // Use the first pickup from the group as anchor
    const anchorPickupId = groupPickupIds[0].toLowerCase();
    this.component.lastSelectedPickupId = anchorPickupId;
    
    // Now handle as normal pickup shift selection
    this.handlePickupShiftSelection(endPickupId);
  };

  // Handle shift selection from pickup to group header
  handlePickupToGroupShiftSelection = (endGroupId) => {
    if (!this.component.gridApi || !this.component.lastSelectedPickupId) {
      this.handleGroupShiftSelection(endGroupId);
      return;
    }

    // Find which group contains the anchor pickup
    const allPickups = this.component.props.DispatchStore.pickupsList || [];
    const anchorPickup = allPickups.find(
      p => (p.pickupId || p.id)?.toLowerCase() === this.component.lastSelectedPickupId
    );

    if (!anchorPickup || !anchorPickup.groupId) {
      this.handleGroupShiftSelection(endGroupId);
      return;
    }

    // Use the group containing the anchor pickup as start point
    this.component.lastSelectedGroupId = anchorPickup.groupId;
    
    // Now handle as normal group shift selection
    this.handleGroupShiftSelection(endGroupId);
  };

  // Handle shift range selection for group headers
  handleGroupShiftSelection = (endGroupId) => {
    if (!this.component.gridApi) return;

    const currentSelectedPickupIds = this.component.props.DispatchStore.selectedPickupIdList || [];
    
    // Find the last selected group (anchor point) - use lastSelectedGroupId or find from current selection
    let lastSelectedGroupId = this.component.lastSelectedGroupId;
    let anchorPickupId = this.component.lastSelectedPickupId; // Track anchor pickup for collapsed group handling
    
    // If no lastSelectedGroupId, try to find it from current selection
    if (!lastSelectedGroupId && currentSelectedPickupIds.length > 0) {
      const allPickups = this.component.props.DispatchStore.pickupsList || [];
      const selectedPickupIdsSet = new Set(currentSelectedPickupIds.map(id => id.toLowerCase()));
      
      // Find the group that contains the last selected pickup (search from end)
      const foundPickup = [...allPickups]
        .reverse()
        .find(pickup => {
          const pickupId = (pickup.pickupId || pickup.id)?.toLowerCase();
          return pickupId && selectedPickupIdsSet.has(pickupId) && pickup.groupId;
        });
      
      if (foundPickup) {
        lastSelectedGroupId = foundPickup.groupId;
        if (!anchorPickupId) {
          anchorPickupId = (foundPickup.pickupId || foundPickup.id)?.toLowerCase();
        }
      }
    }
    
    if (!lastSelectedGroupId) {
      // No anchor point, treat as normal selection
      this.handleGroupSelection(endGroupId, false);
      return;
    }

    // Get all visible rows (both groups and pickups) to determine range
    const visibleRows = this.buildVisibleRows();
    const visibleGroupRows = visibleRows.filter(r => r.type === 'group');

    // Find indices of start and end groups
    let startIndex = visibleGroupRows.findIndex(g => g.groupId === lastSelectedGroupId);
    const endIndex = visibleGroupRows.findIndex(g => g.groupId === endGroupId);

    // If anchor group is not visible (collapsed), use anchor pickup position or group header position
    if (startIndex === -1) {
      let anchorRowIndex = null;
      
      // Try to find anchor pickup in visible rows
      if (anchorPickupId) {
        const anchorPickupRow = visibleRows.find(r => r.type === 'pickup' && r.pickupId === anchorPickupId.toLowerCase());
        if (anchorPickupRow) {
          anchorRowIndex = anchorPickupRow.rowIndex;
        }
      }
      
      // If anchor pickup not found, try to find the anchor group header (should always be visible)
      if (anchorRowIndex === null) {
        const anchorGroupRow = visibleRows.find(r => r.type === 'group' && r.groupId === lastSelectedGroupId);
        if (anchorGroupRow) {
          anchorRowIndex = anchorGroupRow.rowIndex;
        }
      }
      
      if (anchorRowIndex !== null) {
        const endGroupRow = visibleRows.find(r => r.type === 'group' && r.groupId === endGroupId);
        const endRowIndex = endGroupRow ? endGroupRow.rowIndex : null;
        
        if (endRowIndex !== null) {
          // Both groups visible - use normal range calculation
          const minRowIndex = Math.min(anchorRowIndex, endRowIndex);
          const maxRowIndex = Math.max(anchorRowIndex, endRowIndex);
          
          // Collect all pickups in range using optimized helper
          const rangePickupIds = this.collectPickupsInRange(visibleRows, minRowIndex, maxRowIndex, [lastSelectedGroupId, endGroupId]);
          
          this.applyGridSelection(Array.from(rangePickupIds), {
            updateLastSelectedGroup: endGroupId
          });
          return;
        } else if (endIndex === -1) {
          // End group is collapsed - include both groups' pickups
          const rangePickupIds = this.collectPickupIdsFromGroups([lastSelectedGroupId, endGroupId]);
          
          // Also include visible pickups between anchor and end (if any)
          if (anchorRowIndex !== null) {
            visibleRows
              .filter(row => row.type === 'pickup' && row.rowIndex >= anchorRowIndex)
              .forEach((row) => {
                const pickupGroupId = row.groupId;
                if (pickupGroupId && pickupGroupId !== lastSelectedGroupId && pickupGroupId !== endGroupId) {
                  rangePickupIds.add(row.pickupId);
                }
              });
          }
          
          this.applyGridSelection(Array.from(rangePickupIds), {
            updateLastSelectedGroup: endGroupId
          });
          return;
        }
      }
    }

    if (startIndex === -1 || endIndex === -1) {
      // Handle cases where one or both groups are not found in visible group rows
      // This can happen when groups are collapsed and we're selecting from pickup to group
      
      // Try to find group headers in all visible rows (they should always be visible)
      const anchorGroupRow = visibleRows.find(r => r.type === 'group' && r.groupId === lastSelectedGroupId);
      const endGroupRow = visibleRows.find(r => r.type === 'group' && r.groupId === endGroupId);
      
      // If both group headers are found, use their positions
      if (anchorGroupRow && endGroupRow) {
        const anchorRowIndex = anchorGroupRow.rowIndex;
        const endRowIndex = endGroupRow.rowIndex;
        const minRowIndex = Math.min(anchorRowIndex, endRowIndex);
        const maxRowIndex = Math.max(anchorRowIndex, endRowIndex);
        
        // Collect all pickups in range using optimized helper
        const rangePickupIds = this.collectPickupsInRange(visibleRows, minRowIndex, maxRowIndex, [lastSelectedGroupId, endGroupId]);
        
        this.applyGridSelection(Array.from(rangePickupIds), {
          updateLastSelectedGroup: endGroupId
        });
        return;
      }
      
      // If end group is collapsed but start is visible, still include end group's pickups
      if (startIndex !== -1 && endIndex === -1) {
        const rangePickupIds = this.collectPickupIdsFromGroups([lastSelectedGroupId, endGroupId]);
        
        this.applyGridSelection(Array.from(rangePickupIds), {
          updateLastSelectedGroup: endGroupId
        });
        return;
      }
      
      // Fallback to normal selection if indices not found
      this.handleGroupSelection(endGroupId, false);
      return;
    }

    // Determine range (handle both directions)
    const minIndex = Math.min(startIndex, endIndex);
    const maxIndex = Math.max(startIndex, endIndex);
    const rangeGroupIds = visibleGroupRows
      .slice(minIndex, maxIndex + 1)
      .map(g => g.groupId);

    // Collect all pickup IDs in range using optimized helper
    const startGroupRow = visibleGroupRows[startIndex];
    const endGroupRow = visibleGroupRows[endIndex];
    let rangePickupIds;
    
    if (startGroupRow && endGroupRow) {
      const minRowIndex = Math.min(startGroupRow.rowIndex, endGroupRow.rowIndex);
      const maxRowIndex = Math.max(startGroupRow.rowIndex, endGroupRow.rowIndex);
      rangePickupIds = this.collectPickupsInRange(visibleRows, minRowIndex, maxRowIndex, rangeGroupIds);
    } else {
      // Fallback: collect from group IDs only
      rangePickupIds = this.collectPickupIdsFromGroups(rangeGroupIds);
    }

    // Validate: Ensure we have pickup IDs to select
    if (rangePickupIds.size === 0) {
      // Fallback to normal selection
      this.handleGroupSelection(endGroupId, false);
      return;
    }

    this.applyGridSelection(Array.from(rangePickupIds), {
      updateLastSelectedGroup: endGroupId,
      skipValidation: false
    });
  };

  // Select only a single group (clear others first)
  selectSingleGroup = (groupId) => {
    if (!this.component.gridApi) return;

    this.component.isGroupSelectionInProgress = true;
    const groupPickupIds = this.getGroupPickupIds(groupId);
    if (groupPickupIds.length === 0) return;

    const newSelectedPickups = groupPickupIds.map((id) => id.toLowerCase());

    this.component.props.glEventHub.emit('UpdateDispatchStore', 'updateSelectedPickups', [
      newSelectedPickups
    ]);

    // Update last selected group for shift range selection
    this.component.lastSelectedGroupId = groupId;

    setTimeout(() => {
      this.component.groupDataManager.updateGridContext();
      this.updateGridSelectionState();
      this.emitSelectionEvents();

      setTimeout(() => {
        this.component.isGroupSelectionInProgress = false;
      }, 100);
    }, 100);
  };

  // Toggle group selection (select or deselect) - Multi-group selection behavior
  toggleGroupSelection = (groupId, shouldSelect) => {
    if (!this.component.gridApi) return;

    this.component.isGroupSelectionInProgress = true;
    const groupPickupIds = this.getGroupPickupIds(groupId);
    if (groupPickupIds.length === 0) return;

    const currentSelectedPickupIds = this.component.props.DispatchStore.selectedPickupIdList || [];
    // Use Set for O(1) operations instead of array.includes/filter (O(n))
    const selectedSet = new Set(currentSelectedPickupIds.map((id) => id.toLowerCase()));
    const groupPickupIdsSet = new Set(groupPickupIds);

    if (shouldSelect) {
      // Add all group pickup IDs to selection
      groupPickupIdsSet.forEach((id) => selectedSet.add(id));
    } else {
      // Remove all group pickup IDs from selection
      groupPickupIdsSet.forEach((id) => selectedSet.delete(id));
    }

    const newSelectedPickups = Array.from(selectedSet);

    this.component.props.glEventHub.emit('UpdateDispatchStore', 'updateSelectedPickups', [
      newSelectedPickups
    ]);

    setTimeout(() => {
      this.component.groupDataManager.updateGridContext();
      this.updateGridSelectionState();
      this.emitSelectionEvents();

      setTimeout(() => {
        this.component.isGroupSelectionInProgress = false;
      }, 100);
    }, 100);
  };

  // Emit selection events (helper function)
  emitSelectionEvents = () => {
    // Only emit if selection originated from grid, not from map
    if (this.component.pickupSelectedOnGrid) {
      this.component.props.glEventHub.emit(appConstants.Event.PICKUP_SELECTED_GRID, {
        pickupsData: this.component.props.DispatchStore.selectedPickupsList
      });
    }
    this.component.updatePickupManagementIconStatus();

    // Emit driver selection event
    this.component.props.glEventHub.emit(appConstants.Event.SELECT_PICKUP_ROUTES, true);

    // Also emit driver selection with driver IDs from selected pickups
    const selectedPickups = this.component.props.DispatchStore.selectedPickupsList || [];
    const selectedDriverIds = selectedPickups.map((pickup) => pickup.driverId).filter(Boolean);

    if (selectedDriverIds.length > 0) {
      const uniqueDriverIds = [...new Set(selectedDriverIds)];
      this.component.props.glEventHub.emit('SelectDriverRoutes', uniqueDriverIds);
    }
  };

  // Check if a group is selected (all pickups in group are selected) - now computed
  isGroupSelected = (groupId) => {
    const currentSelectedPickupIds = this.component.props.DispatchStore.selectedPickupIdList || [];
    const groupPickupIds = this.getGroupPickupIds(groupId);

    if (groupPickupIds.length === 0) return false;

    // Use Set for O(1) lookup instead of array.includes (O(n))
    const selectedPickupIdsSet = new Set(currentSelectedPickupIds.map((id) => id.toLowerCase()));
    
    // Check if all pickups in this group are selected
    return groupPickupIds.every(pickupId => selectedPickupIdsSet.has(pickupId));
  };

  onSelectionChanged = (event) => {
    if (!this.component.gridApi || this.component.isGroupSelectionInProgress || this.component.isExpandCollapseInProgress) return;

    // Get current grid selection - must use forEachNode to include hidden nodes (collapsed groups)
    const gridSelectedIds = [];
    this.component.gridApi.forEachNodeAfterFilterAndSort((node) => {
      if (node.isSelected()) {
        const rowData = node.data;
        if (isGroupedPickups(rowData) || isSingleInGroupPickups(rowData)) {
          const pickupId = rowData.pickupId || rowData.id;
          if (pickupId) gridSelectedIds.push(pickupId.toLowerCase());
        } else if (isGroupHeaders(rowData) && rowData.pickupNumberIdList) {
          rowData.pickupNumberIdList.forEach((p) => {
            if (p.pickupId) gridSelectedIds.push(p.pickupId.toLowerCase());
          });
        }
      }
    });

    // Get current store selection
    const storeSelectedIds = this.component.props.DispatchStore.selectedPickupIdList || [];

    // Sort arrays for comparison (don't mutate originals)
    const sortedGridIds = [...gridSelectedIds].sort((a, b) => a.localeCompare(b));
    const sortedStoreIds = [...storeSelectedIds].sort((a, b) => a.localeCompare(b));

    // Only update store if grid and store selections don't match
    // This prevents circular updates while ensuring consistency
    if (JSON.stringify(sortedGridIds) !== JSON.stringify(sortedStoreIds)) {
      this.processUserSelection();
    }
    if (this.component.pendingEditPickup) {
      this.component.pendingEditPickup = false;
      this.component.openPickupManagement({ action: PICKUP_MANAGEMENT_MODES.EDIT_PICKUP });
    }
  };

  // Extract and process all selected pickup IDs from grid
  processUserSelection = () => {
    const selectedPickupIds = [];
    const selectedDriverIds = [];

    // Must use forEachNodeAfterFilterAndSort to include hidden nodes (collapsed groups)
    this.component.gridApi.forEachNodeAfterFilterAndSort((node) => {
      if (node.isSelected()) {
        const rowData = node.data;
        if (isGroupedPickups(rowData) || isSingleInGroupPickups(rowData)) {
          const pickupId = rowData.pickupId || rowData.id;
          if (pickupId) {
            selectedPickupIds.push(pickupId.toLowerCase());
            // Add driver ID if pickup is assigned
            if (rowData.driverId) {
              selectedDriverIds.push(rowData.driverId);
            }
          }
        } else if (isGroupHeaders(rowData) && rowData.pickupNumberIdList) {
          // Include all pickups in the group
          rowData.pickupNumberIdList.forEach((p) => {
            if (p.pickupId) {
              selectedPickupIds.push(p.pickupId.toLowerCase());
              // Add driver ID if pickup is assigned
              if (p.driverId) {
                selectedDriverIds.push(p.driverId);
              }
            }
          });
        }
      }
    });

    // Update store and emit events
    this.component.props.glEventHub.emit('UpdateDispatchStore', 'updateSelectedPickups', [selectedPickupIds]);
    // Only emit if selection originated from grid, not from map
    if (this.component.pickupSelectedOnGrid) {
      this.component.props.glEventHub.emit(appConstants.Event.PICKUP_SELECTED_GRID, {
        pickupsData: this.component.props.DispatchStore.selectedPickupsList
      });
    }

    // Update grid selection state first to ensure getSelectedRows() returns correct data
    this.updateGridSelectionState();
    this.component.updatePickupManagementIconStatus();

    if (this.component.pendingEditPickup) {
      this.component.openPickupManagement({ action: PICKUP_MANAGEMENT_MODES.EDIT_PICKUP });
      // Clear both pending edit flags after opening the popup
      this.component.pendingEditPickup = false;
      this.component.pendingEditPickupId = null;
    }

    this.component.props.glEventHub.emit(appConstants.Event.SELECT_PICKUP_ROUTES, true);

    // Emit driver selection event with unique driver IDs
    if (selectedDriverIds.length > 0) {
      const uniqueDriverIds = [...new Set(selectedDriverIds)];
      this.component.props.glEventHub.emit('SelectDriverRoutes', uniqueDriverIds);
    }
  };

  // Handle row clicks - unified for all row types
  onRowClicked = (event) => {
    this.handleSelection(event);
  };

  // Prevent group rows from being selectable
  isRowSelectable = (node) => {
    // Only allow selection of pickup rows, not group headers
    return !isGroupHeaders(node.data);
  };

  onPickupSelectedOnMap = (selectedPickupIdList, refreshDriverSelection = true) => {
    if (this.component.gridApi) {
      this.component.pickupSelectedOnGrid = false;

      const allPickups = this.component.props.DispatchStore.pickupsList || [];
      
      // Build pickup lookup map for O(1) access - single pass O(n)
      const pickupMap = new Map();
      const groupPickupsMap = new Map(); // groupId -> Set of pickupIds
      
      allPickups.forEach((pickup) => {
        if (pickup.pickupId) {
          pickupMap.set(pickup.pickupId.toLowerCase(), pickup);
          if (pickup.groupId) {
            if (!groupPickupsMap.has(pickup.groupId)) {
              groupPickupsMap.set(pickup.groupId, new Set());
            }
            groupPickupsMap.get(pickup.groupId).add(pickup.pickupId.toUpperCase());
          }
        }
      });

      // Find groups that contain the selected pickups - O(m) where m = selectedPickupIdList.length
      const selectedGroups = new Set();
      
      selectedPickupIdList.forEach((pickupId) => {
        const pickup = pickupMap.get(pickupId.toLowerCase());
        if (pickup && pickup.groupId) {
          selectedGroups.add(pickup.groupId);
        }
      });

      // Expand groups and update group selection state using unique IDs
      if (selectedGroups.size > 0) {
        // Get all pickup IDs from selected groups for store update - O(n)
        const allSelectedPickupIdsSet = new Set();
        selectedGroups.forEach((groupId) => {
          const groupPickupIds = groupPickupsMap.get(groupId);
          if (groupPickupIds) {
            groupPickupIds.forEach((pickupId) => {
              allSelectedPickupIdsSet.add(pickupId.toLowerCase());
            });
          }
        });

        const allSelectedPickupIds = Array.from(allSelectedPickupIdsSet);

        // Update dispatch store with all pickup IDs from selected groups
        this.component.props.glEventHub.emit('UpdateDispatchStore', 'updateSelectedPickups', [
          allSelectedPickupIds
        ]);

        this.component.setState(
          (prevState) => {
            const newExpandedGroups = new Set(prevState.expandedGroups);

            // Expand all selected groups (group selection is now computed)
            selectedGroups.forEach((groupId) => {
              newExpandedGroups.add(groupId);
            });

            return {
              expandedGroups: newExpandedGroups
            };
          },
          () => {
            // After state update, force refresh to ensure visual updates
            if (this.component.gridApi) {
              this.component.gridApi.refreshCells({ force: true });
            }
          }
        );
        this.component.updatePickupManagementIconStatus();
      }

      // Update individual pickup selection in grid using unique IDs - select ALL pickups from selected groups
      const allSelectedPickupIdsUpper = new Set();
      selectedGroups.forEach((groupId) => {
        const groupPickupIds = groupPickupsMap.get(groupId);
        if (groupPickupIds) {
          groupPickupIds.forEach((pickupId) => allSelectedPickupIdsUpper.add(pickupId));
        }
      });

      this.component.gridApi.forEachNode((node) => {
        // Use unique ID for selection comparison
        const nodeId = node.data.id;
        if (nodeId && allSelectedPickupIdsUpper.has(nodeId.toUpperCase())) {
          node.setSelected(true, false);
          this.component.gridApi.ensureIndexVisible(node.rowIndex);
        } else {
          node.setSelected(false, false);
        }
      });

      // Emit driver selection events if refreshDriverSelection is enabled
      if (refreshDriverSelection) {
        // Emit driver selection event
        this.component.props.glEventHub.emit(appConstants.Event.SELECT_PICKUP_ROUTES, true);

        // Also emit driver selection with driver IDs from selected pickups
        const selectedPickups = this.component.props.DispatchStore.selectedPickupsList || [];
        const selectedDriverIds = selectedPickups.map((pickup) => pickup.driverId).filter(Boolean);

        if (selectedDriverIds.length > 0) {
          const uniqueDriverIds = [...new Set(selectedDriverIds)];
          this.component.props.glEventHub.emit('SelectDriverRoutes', uniqueDriverIds);
        }
      }

      // Force refresh cells to update visual styling
      this.component.gridApi.refreshCells({ force: true });
      this.component.gridApi.dispatchEvent({
        type: 'selectionChanged',
        api: this.component.gridApi,
        refreshDriverSelection: refreshDriverSelection
      });
    }
  };

  selectPickups = () => {
    if (this.component.gridApi?.forEachNodeAfterFilterAndSort) {
      // Use Set for O(1) lookup instead of array.includes (O(n))
      const selectedPickupIdsSet = new Set(
        this.component.props.DispatchStore.selectedPickupIdList.map((id) => id.toLowerCase())
      );

      // Get all nodes including invisible ones (collapsed groups)
      this.component.gridApi.forEachNodeAfterFilterAndSort((node) => {
        const isPickupRow = isGroupedPickups(node.data) || isSingleInGroupPickups(node.data);
        const isGroupRow = isGroupHeaders(node.data);

        if (isPickupRow) {
          // Use pickupId for grouped pickups, id for single-in-group pickups
          const pickupId = node.data.pickupId || node.data.id;
          const isSelected = selectedPickupIdsSet.has(pickupId.toLowerCase());

          if (isSelected) {
            node.setSelected(true, false); // props are isSelected, clearOtherSelection
            // Only ensure visibility for visible nodes
            if (node.rowIndex !== undefined) {
              this.component.gridApi.ensureIndexVisible(node.rowIndex);
            }
          } else {
            node.setSelected(false, false);
          }
        } else if (isGroupRow) {
          // Group rows are not selectable
          node.setSelected(false, false);
        }
      });
    }
  };

  // Update grid visual selection state using AG-Grid's built-in APIs
  updateGridSelectionState = () => {
    if (!this.component.gridApi?.forEachNodeAfterFilterAndSort) return;

    const selectedPickupIdsSet = new Set(
      this.component.props.DispatchStore.selectedPickupIdList.map((id) => id.toLowerCase())
    );

    // Use AG-Grid's forEachNodeAfterFilterAndSort for visible nodes
    this.component.gridApi.forEachNodeAfterFilterAndSort((node) => {
      const rowData = node.data;
      
      if (isGroupHeaders(rowData)) {
        // Group headers should NEVER be selected
        node.setSelected(false, false);
      } else if (isGroupedPickups(rowData) || isSingleInGroupPickups(rowData)) {
        const pickupId = (rowData.pickupId || rowData.id)?.toLowerCase();
        node.setSelected(pickupId ? selectedPickupIdsSet.has(pickupId) : false, false);
      }
    });

    // Refresh cells for styling updates
    this.component.gridApi.refreshCells({ force: true });
  };
}

